import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSetAtom } from "jotai";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faFileLines, faClipboardList, faTimes } from "@fortawesome/free-solid-svg-icons";
import { financialApplicationIdAtomWithStorage } from "@/atoms/financialApplicationIdAtom";
import { useConsumerPortal } from "@/hooks/useConsumerPortal";
import { useAuthHeadersRequired } from "@/lib/auth/authUtils";
import { RequireAuth } from "@/lib/auth/RequireAuth";
import {
  BoltTaskCompactGrid,
  openTaskExecutionDialog,
  TaskExecutionModuleFormRegistry,
  TaskExecutionModuleRegistry,
} from "@bolt/task";
import { DocumentTemplateTaskExecutionTypeModule } from "@bolt/documenttemplates";
import { FinancialApplicationExpressionSchemaTaskExecutionTypeForm } from "@/components/TaskExecutionTypeForms/FinancialApplicationExpressionSchemaTaskExecutionTypeForm";
import { JointAccountOwnerExpressionSchemaTaskExecutionTypeForm } from "@/components/TaskExecutionTypeForms/JointAccountOwnerExpressionSchemaTaskExecutionTypeForm";
import { KeyPersonExpressionSchemaTaskExecutionTypeForm } from "@/components/TaskExecutionTypeForms/KeyPersonExpressionSchemaTaskExecutionTypeForm";
import variables from "@/config/variables";
import "./ConsumerPortalPage.scss";

export const ConsumerPortalPage: React.FC = () => {
  return (
    <RequireAuth clientType="consumer">
      <ConsumerPortalPageContent />
    </RequireAuth>
  );
};

const ConsumerPortalPageContent: React.FC = () => {
  const navigate = useNavigate();
  const setFinancialApplicationId = useSetAtom(financialApplicationIdAtomWithStorage);
  const { metrics, refetch } = useConsumerPortal();
  const getAuthRequestHeader = useAuthHeadersRequired();
  const [searchParams, setSearchParams] = useSearchParams();
  const [bannerDismissed, setBannerDismissed] = useState(false);

  const formRegistry: TaskExecutionModuleFormRegistry = {
    FinancialApplicationExpressionSchema: {
      FormComponent: FinancialApplicationExpressionSchemaTaskExecutionTypeForm,
    },
  };

  const taskExecutionTypeModuleRegistry: TaskExecutionModuleRegistry = {
    DocumentTemplateTaskExecutionType: DocumentTemplateTaskExecutionTypeModule,
  };

  // Handle taskId URL parameter
  useEffect(() => {
    const taskId = searchParams.get("taskId");
    if (taskId) {
      openTaskExecutionDialog({
        taskId,
        apiBaseUrl: variables.apiBaseUrl,
        getAuthRequestHeader,
        formRegistry,
        taskExecutionModuleRegistry: taskExecutionTypeModuleRegistry,
        commentPermissions: {
          canComment: true,
          canEdit: false,
          canPin: false,
          canDelete: false,
        },
        handleTaskComplete: (_taskId: string) => {
          return handleTaskComplete();
        },
      });
    }
  }, [searchParams, getAuthRequestHeader, setSearchParams, variables.apiBaseUrl]);

  const handleStartNewApplication = () => {
    // Clear the financial application ID to force creation of a new one
    setFinancialApplicationId(undefined);
    // Navigate to the home page
    navigate("/");
  };

  const handleViewApplications = () => {
    // Navigate to the applications page
    navigate("/customer/portal/applications");
  };

  const handleTaskComplete = () => {
    return refetch();
  };

  const handleDismissBanner = () => {
    setBannerDismissed(true);
  };

  const remainingTasks = metrics?.pendingTasks ?? 0;
  const showBanner = remainingTasks > 0 && !bannerDismissed;

  return (
    <div className="consumer-portal-page">
      <div className="consumer-portal-page__header">
        <div className="consumer-portal-page__header-content">
          <div className="consumer-portal-page__welcome">
            <h1 className="consumer-portal-page__title">Welcome Back</h1>
            <p className="consumer-portal-page__subtitle">
              Manage your financial applications and account information
            </p>
          </div>
          <div className="consumer-portal-page__header-actions">
            <button
              className="consumer-portal-page__header-button consumer-portal-page__header-button--secondary"
              onClick={handleViewApplications}
            >
              <FontAwesomeIcon icon={faFileLines} />
              View Applications
            </button>
            <button
              className="consumer-portal-page__header-button consumer-portal-page__header-button--primary"
              onClick={handleStartNewApplication}
            >
              <FontAwesomeIcon icon={faPlus} />
              Start New Application
            </button>
          </div>
        </div>
      </div>

      {showBanner && (
        <div className="consumer-portal-page__banner">
          <div className="consumer-portal-page__banner-content">
            <div className="consumer-portal-page__banner-icon">
              <FontAwesomeIcon icon={faClipboardList} />
            </div>
            <div className="consumer-portal-page__banner-text">
              <h3>
                You have {remainingTasks} remaining task{remainingTasks !== 1 ? "s" : ""}
              </h3>
              <p>Click on a task in the table below to complete it.</p>
            </div>
            <button
              className="consumer-portal-page__banner-dismiss"
              onClick={handleDismissBanner}
              aria-label="Dismiss banner"
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
        </div>
      )}

      <div className="consumer-portal-page__content">
        <div className="consumer-portal-page__main-section">
          <h2 className="consumer-portal-page__section-title">My Tasks</h2>
          <p className="consumer-portal-page__section-description">
            Click on any task below to open and complete it. Tasks must be completed to move your
            application forward.
          </p>
          <div className="consumer-portal-page__task-grid-container">
            <BoltTaskCompactGrid
              apiBaseUrl={variables.apiBaseUrl}
              gridKey="consumer-tasks-grid"
              getAuthRequestHeader={getAuthRequestHeader}
              className="consumer-portal-tasks-grid"
              isTaskOverviewReadOnly={true}
              formRegistry={{
                FinancialApplicationExpressionSchema: {
                  FormComponent: FinancialApplicationExpressionSchemaTaskExecutionTypeForm,
                },
                JointAccountOwnerExpressionSchema: {
                  FormComponent: JointAccountOwnerExpressionSchemaTaskExecutionTypeForm,
                },
                KeyPersonExpressionSchema: {
                  FormComponent: KeyPersonExpressionSchemaTaskExecutionTypeForm,
                },
              }}
              taskExecutionModuleRegistry={{
                DocumentTemplateTaskExecutionType: DocumentTemplateTaskExecutionTypeModule,
              }}
              taskExecutionLeftPanelIsDocked={true}
              taskExecutionRightPanelIsDocked={false}
              commentPermissions={{
                canComment: true,
                canEdit: false,
                canPin: false,
                canDelete: false,
              }}
              onTaskComplete={handleTaskComplete}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConsumerPortalPage;
